import { ChevronRight, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

/**
 * Horizontal trapezoidal funnel with 8 stages.
 * Each row: label (left) · absolute count (center bold) · conv from prev
 * Clickable — calls `onStageClick(stage)` with the stage key.
 */
export const STAGE_META = [
  { key: "leads_received",  label: "Leads recebidos",  conv: null },
  { key: "leads_contacted", label: "Contactados",      conv: "lead_to_contact" },
  { key: "leads_qualified", label: "Qualificados",     conv: "contact_to_qualified" },
  { key: "estimates_sent",  label: "Estimativas enviadas", conv: "qualified_to_estimate" },
  { key: "rt_scheduled",    label: "RT agendadas",     conv: "estimate_to_rt" },
  { key: "rt_realised",     label: "RT realizadas",    conv: "rt_scheduled_to_realised" },
  { key: "proposals_sent",  label: "Propostas enviadas", conv: "rt_to_proposal" },
  { key: "deals_closed",    label: "Negócios fechados", conv: "proposal_to_deal" },
];

const CLASS_COLOR = {
  excellent: "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border-[hsl(var(--success))]/25",
  target:    "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border-[hsl(var(--success))]/25",
  attention: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25",
  critical:  "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/25",
  unknown:   "bg-muted/40 text-muted-foreground border-border",
};

const CLASS_DOT = {
  excellent: "bg-emerald-500",
  target: "bg-emerald-500",
  attention: "bg-amber-500",
  critical: "bg-red-500",
  unknown: "bg-[#94A3B8]",
};

function DeltaBadge({ delta }) {
  if (!delta || delta.pct === null || delta.pct === undefined) return null;
  const Icon = delta.direction === "up" ? ArrowUpRight : delta.direction === "down" ? ArrowDownRight : Minus;
  const color = delta.direction === "up" ? "text-[hsl(var(--success))]" : delta.direction === "down" ? "text-[hsl(var(--danger))]" : "text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${color}`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {Math.abs(delta.pct).toFixed(1)}%
    </span>
  );
}

export default function FunnelChart({ data, onStageClick, onLossClick, hideStages = [] }) {
  if (!data) return null;
  const counts = data.counts || {};
  const conversions = data.conversions || {};
  const classifications = data.classifications || {};
  const deltas = data.deltas || {};

  const stages = STAGE_META.filter(s => !hideStages.includes(s.key));
  const maxCount = Math.max(1, ...stages.map(s => counts[s.key] || 0));

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="funnel-chart">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Funil de conversão</div>
          <h3 className="font-display text-2xl">Da entrada ao negócio</h3>
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Objectivo</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Atenção</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Crítico</span>
        </div>
      </div>

      <div className="space-y-1">
        {stages.map((stage, idx) => {
          const count = counts[stage.key] || 0;
          const width = Math.max(20, Math.round((count / maxCount) * 100));
          const cls = stage.conv ? classifications[stage.conv] : "unknown";
          const delta = deltas[stage.key];
          const convPct = stage.conv ? conversions[stage.conv] : null;
          const nextStage = stages[idx + 1];
          const lossCount = nextStage ? Math.max(0, count - (counts[nextStage.key] || 0)) : 0;

          return (
            <div key={stage.key}>
              {/* Stage bar */}
              <button
                onClick={() => onStageClick && onStageClick(stage.key)}
                className="group w-full text-left rounded-xl border border-border hover:border-primary/50 hover:bg-muted/40 transition-all p-4 flex items-center gap-4"
                data-testid={`funnel-stage-${stage.key}`}
              >
                <div className="w-40 flex-shrink-0">
                  <div className="text-xs text-muted-foreground mb-1">{stage.label}</div>
                  <div className="font-display text-3xl text-foreground tracking-tight">{count.toLocaleString("pt-PT")}</div>
                  <DeltaBadge delta={delta} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="h-8 bg-muted/40 rounded-lg overflow-hidden relative">
                    <div
                      className="h-full transition-all duration-700 rounded-lg"
                      style={{
                        width: `${width}%`,
                        background: `linear-gradient(90deg, #0F172A 0%, #1A3D6E ${width}%)`,
                      }}
                    />
                  </div>
                </div>

                {stage.conv && (
                  <div className={`w-24 flex-shrink-0 text-center px-3 py-2 rounded-lg border ${CLASS_COLOR[cls]}`}>
                    <div className="text-[10px] uppercase tracking-[0.1em] font-bold opacity-70">{"conv"}</div>
                    <div className="font-display text-lg leading-none mt-0.5">{convPct?.toFixed(1)}%</div>
                  </div>
                )}

                <ChevronRight className="w-4 h-4 text-muted-foreground/70 group-hover:text-foreground flex-shrink-0" />
              </button>

              {/* Loss indicator between stages */}
              {nextStage && lossCount > 0 && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onLossClick && onLossClick(nextStage.key)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onLossClick && onLossClick(nextStage.key); }}
                  className="ml-40 pl-4 py-1 flex items-center gap-2 text-xs text-[hsl(var(--danger))] hover:underline cursor-pointer"
                  data-testid={`funnel-loss-${nextStage.key}`}
                >
                  <span className="w-px h-3 bg-muted" />
                  <span>↓ {lossCount.toLocaleString("pt-PT")} não avançaram</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
